import type { ComissaoPercentual, Produto, RegraComissao } from '../db/tipos'
import type { LinhaParaComissao } from './passoComissao'

export interface EstadoDaPolitica {
  // A regra da PROPRIA tabela (tabela_preco_id = esta tabela), quando existe.
  regraDaTabela: RegraComissao | undefined
  // A regra padrao da fornecedora (tabela_preco_id = null), quando existe.
  regraDaFornecedora: RegraComissao | undefined
  percentuaisDaTabela: ComissaoPercentual[]
  percentuaisDaFornecedora: ComissaoPercentual[]
  produtosComPrecoNaTabela: Produto[]
  linhas: LinhaParaComissao[]
}

export type Politica =
  | { perguntar: false }
  | {
      perguntar: true
      // true = a tabela ja paga alguma coisa por conta propria, e a pergunta
      // vira "manter a desta tabela ou seguir a fornecedora?". false = a
      // pergunta e so "seguir a fornecedora?".
      jaTemComissaoPropria: boolean
      // Quantos produtos a escolha afeta. Vai para a tela: "60 produtos".
      produtosDaTabela: number
      // O % fixo da fornecedora, quando ela tem um. null = ela nao paga um %
      // fixo hoje.
      pctDaFornecedora: number | null
      // Se o passo pode oferecer o campo para digitar o % da fornecedora.
      // false quando gravar um % fixo APAGARIA percentuais por produto que ja
      // pagam -- exatamente a classe de defeito que este projeto combate.
      podeDefinirPctDaFornecedora: boolean
    }

// Uma regra "paga alguma coisa" quando o motor consegue tirar um numero dela
// sem cair no `?? 0`. Zero EXPLICITO conta: e escolha, nao esquecimento.
function pagaAlgo(
  regra: RegraComissao | undefined,
  percentuais: ComissaoPercentual[],
): boolean {
  if (!regra) return false
  if (regra.tipo === 'pct_fixo') return regra.pct_padrao !== null
  return percentuais.some((p) => p.regra_id === regra.id && p.apagado_em === null)
}

// A politica da tabela: seguir a fornecedora, ou ter comissao propria?
//
// Nasceu do pedido dela em 26/08: "a comissao da fornecedora so vai valer se a
// tabela importada nao tiver o campo de comissao em nenhum produto, ai pode ter
// a pergunta 'tabela sem campo comissao deseja seguir regra fixa da
// fornecedora?'".
//
// Por que e uma funcao SEPARADA de passoDeComissaoDaImportacao: sao duas
// perguntas diferentes. Esta decide QUEM governa a tabela; a outra decide QUEM
// esta descoberto dentro do governo escolhido. Juntar as duas foi o que produziu
// o bug de 26/08 -- uma funcao que respondia duas perguntas acabou respondendo
// mal as duas.
export function politicaDaTabela(estado: EstadoDaPolitica): Politica {
  const {
    regraDaTabela, regraDaFornecedora,
    percentuaisDaTabela, percentuaisDaFornecedora,
    produtosComPrecoNaTabela, linhas,
  } = estado

  // Se UMA linha traz comissao, a planilha ja disse qual e a politica desta
  // importacao. Perguntar seria desautorizar o que ela mandou no arquivo.
  if (linhas.some((l) => l.temComissao)) return { perguntar: false }

  // Sem preco em jogo nao ha comissao a decidir. Alarme sem consequencia
  // ensina a ignorar o alarme.
  const haPrecoEmJogo =
    produtosComPrecoNaTabela.length > 0 || linhas.some((l) => l.temPreco)
  if (!haPrecoEmJogo) return { perguntar: false }

  const daFornecedoraEhPorItem =
    regraDaFornecedora !== undefined && regraDaFornecedora.tipo === 'pct_por_item'
  const fornecedoraJaPagaPorProduto =
    daFornecedoraEhPorItem && pagaAlgo(regraDaFornecedora, percentuaisDaFornecedora)

  return {
    perguntar: true,
    jaTemComissaoPropria: pagaAlgo(regraDaTabela, percentuaisDaTabela),
    produtosDaTabela: produtosComPrecoNaTabela.length,
    pctDaFornecedora:
      regraDaFornecedora?.tipo === 'pct_fixo' ? regraDaFornecedora.pct_padrao : null,
    // Gravar um % fixo por cima de uma regra que ja paga POR PRODUTO faria
    // esses percentuais pararem de valer em silencio. Nesse caso o passo nao
    // oferece o campo -- e a tela explica por que.
    podeDefinirPctDaFornecedora: !fornecedoraJaPagaPorProduto,
  }
}
